import { useEffect, useState } from "react";
import logoSoxy from "@/assets/customer-logos/soxy.svg";
import logoChange from "@/assets/customer-logos/change.svg";
import logoSpeechaim from "@/assets/customer-logos/speechaim.svg";
import logoRampup from "@/assets/customer-logos/rampup.svg";
import logoInfluencer from "@/assets/customer-logos/influencer.svg";
import logoGenpal from "@/assets/customer-logos/genpal.svg";
import claudeCodeMark from "@/assets/runtime-marks/claude-code.svg";
import codexMark from "@/assets/runtime-marks/codex.svg";
import cursorMark from "@/assets/runtime-marks/cursor.svg";
import antigravityMark from "@/assets/runtime-marks/antigravity.png";
import grokMark from "@/assets/runtime-marks/grok.webp";
import manusMark from "@/assets/runtime-marks/manus.svg";
import openclawMark from "@/assets/runtime-marks/openclaw.svg";
import hermesMark from "@/assets/runtime-marks/hermes.png";
import replitMark from "@/assets/runtime-marks/replit.svg";
import perplexityMark from "@/assets/runtime-marks/perplexity.svg";
import lovableMark from "@/assets/runtime-marks/lovable.svg";

/**
 * Mobile-only trust rotator. Replaces the LogoStrip and Runs-on row
 * on small screens with a single pill-card that cycles between
 * three widgets: brand logos, a star-rating trust line, and the
 * runtimes + SOC strip. Inspired by the hmu.co stacked cards style.
 */

const COMPANIES = [
  { name: "SOXY", logo: logoSoxy },
  { name: "CHANGE", logo: logoChange },
  { name: "SPEECHAIM", logo: logoSpeechaim },
  { name: "RAMPUP.AI", logo: logoRampup },
  { name: "INFLUENCER.NEW", logo: logoInfluencer },
  { name: "GENPAL", logo: logoGenpal },
];

const RUNTIMES = [
  { name: "Claude Code", src: claudeCodeMark },
  { name: "Codex", src: codexMark },
  { name: "Cursor", src: cursorMark },
  { name: "Antigravity", src: antigravityMark },
  { name: "Grok", src: grokMark },
  { name: "Manus", src: manusMark },
  { name: "OpenClaw", src: openclawMark },
  { name: "Hermes", src: hermesMark },
  { name: "Replit", src: replitMark },
  { name: "Perplexity", src: perplexityMark },
  { name: "Lovable", src: lovableMark },
];

const SLIDE_MS = 9000;

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-foreground/45 leading-none">
    {children}
  </p>
);


const LovedCard = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setI((v) => (v + 1) % COMPANIES.length),
      1500,
    );
    return () => window.clearInterval(id);
  }, []);
  const c = COMPANIES[i];
  return (
    <div className="flex flex-col items-center gap-2">
      <Eyebrow>Loved by many</Eyebrow>
      <div className="relative flex items-center justify-center" style={{ width: 160, height: 22 }}>
        <img
          key={c.name}
          src={c.logo}
          alt={c.name}
          loading="lazy"
          decoding="async"
          className="animate-fade-in opacity-80 [filter:brightness(0)] dark:opacity-90 dark:[filter:brightness(0)_invert(1)] max-h-full max-w-full"
          style={{ height: 22, width: "auto", objectFit: "contain" }}
        />
      </div>
    </div>
  );
};

const StarsCard = () => (
  <div className="flex flex-col items-center gap-1.5">
    <div className="flex items-center gap-[3px] text-[#ffbf24]">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M12 2l2.95 6.36 6.95.62-5.2 4.7 1.55 6.82L12 17.27 5.75 20.5l1.55-6.82-5.2-4.7 6.95-.62L12 2z" />
        </svg>
      ))}
    </div>
    <p className="font-sans text-[12px] font-medium text-foreground leading-none">
      Loved by operators across industries
    </p>

  </div>
);

const RunsOnCard = () => {
  const VISIBLE = 6;
  const shown = RUNTIMES.slice(0, VISIBLE);
  const extra = RUNTIMES.length - VISIBLE;
  return (
    <div className="flex flex-col items-center gap-2">
      <Eyebrow>Runs on</Eyebrow>
      <div className="flex items-center gap-3">
        {shown.map((r) => (
          <span key={r.name} className="group/rt relative inline-flex items-center justify-center">
            <img
              src={r.src}
              alt={r.name}
              width={28}
              height={14}
              className="h-[14px] w-auto opacity-70 [filter:brightness(0)] dark:opacity-85 dark:[filter:brightness(0)_invert(1)] transition-opacity group-hover/rt:opacity-100"
            />
            <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-[4px] bg-black/90 text-white text-[9.5px] font-medium px-1.5 py-0.5 opacity-0 group-hover/rt:opacity-100 transition-opacity z-50" style={{ fontFamily: "var(--font-mono, ui-monospace, monospace)" }}>
              {r.name}
            </span>
          </span>
        ))}
        {extra > 0 && (
          <span className="group/more relative inline-flex items-center justify-center font-mono text-[10px] leading-none text-foreground/70 hover:text-foreground transition-colors px-1.5 h-[14px] rounded-[3px] bg-foreground/[0.06]">
            +{extra}
            <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-[4px] bg-black/90 text-white text-[9.5px] font-medium px-1.5 py-0.5 opacity-0 group-hover/more:opacity-100 transition-opacity z-50" style={{ fontFamily: "var(--font-mono, ui-monospace, monospace)" }}>
              {RUNTIMES.slice(VISIBLE).map((r) => r.name).join(" · ")}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

const ComplianceCard = () => (
  <div className="flex flex-col items-center gap-2">
    <Eyebrow>Compliance</Eyebrow>
    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-foreground/75 leading-none">
      SOC 2 · GDPR · CCPA
    </p>
    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-foreground/40 leading-none">
      in progress
    </p>

  </div>
);

const SLIDES = [LovedCard, StarsCard, RunsOnCard, ComplianceCard];

const ROLES = [
  "coding",
  "code review",
  "product manager",
  "designer",
  "researcher",
  "marketer",
  "growth",
  "sales",
  "SDR",
  "support",
  "ops",
  "recruiter",
  "data analyst",
  "QA",
  "DevOps",
  "security",
  "finance",
  "legal",
  "content",
  "social",
  "SEO",
  "PR",
  "brand",
  "BI",
  "customer success",
];

const ROLE_MS = 3800;

const RoleRotator = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setI((v) => (v + 1) % ROLES.length),
      ROLE_MS,
    );
    return () => window.clearInterval(id);
  }, []);
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 leading-none mb-2 text-center min-h-[12px]">
      {ROLES.map((r, idx) => (
        <span
          key={r}
          aria-hidden={i !== idx}
          className="block transition-opacity duration-500"
          style={{
            opacity: i === idx ? 1 : 0,
            display: i === idx ? "block" : "none",
          }}
        >
          Mane is the best <span className="text-white/85">{r}</span> agent
        </span>
      ))}
    </p>
  );
};

const MobileTrustRotator = () => {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setSlide((v) => (v + 1) % SLIDES.length),
      SLIDE_MS,
    );
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="w-[min(320px,calc(100vw-2.5rem))]">
      {/* RoleRotator removed — the hero H1 now owns the "Mane is the best <role> agent" phrase. */}
      <div className="relative rounded-[14px] p-[1.5px] overflow-hidden isolate">
        {/* Energy ring: static mask clips the rotating conic gradient to
            a 1.5px ring around the perimeter so it never bleeds inside
            the translucent box. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[14px] overflow-hidden z-0"
          style={{
            padding: "1.5px",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            maskComposite: "exclude",
          }}
        >
          <span
            className="absolute -inset-[60%] block animate-[trust-energy-spin_4s_linear_infinite]"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, transparent 280deg, hsl(var(--accent) / 0.0) 290deg, hsl(var(--accent) / 0.95) 330deg, #fff 350deg, hsl(var(--accent) / 0.95) 10deg, hsl(var(--accent) / 0.0) 60deg, transparent 70deg, transparent 360deg)",
              filter: "blur(1.2px)",
            }}
          />
        </div>
        <div className="relative z-10 rounded-[12.5px] bg-white/35 dark:bg-black/40 backdrop-blur-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)] px-5 py-3 min-h-[74px] flex items-center justify-center overflow-hidden">
          {SLIDES.map((Slide, i) => (
            <div
              key={i}
              className="absolute inset-0 flex items-center justify-center transition-opacity duration-500"
              style={{ opacity: slide === i ? 1 : 0, pointerEvents: slide === i ? "auto" : "none" }}
              aria-hidden={slide !== i}
            >
              <Slide />
            </div>
          ))}
          {/* Dot indicator */}
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className="h-[3px] rounded-full transition-all duration-300 bg-foreground/20 data-[active=true]:bg-foreground/65"
                data-active={slide === i}
                style={{ width: slide === i ? 12 : 3 }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileTrustRotator;
